import { formatLabel, isVideo, bustUrl, isFeedVisible } from "@/lib/constants";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Clapperboard, Images, Image as ImageIcon, Circle, FileText, Quote, Sparkles } from "lucide-react";

const FORMAT_ICONS = {
  reel: Clapperboard, carrousel: Images, photo: ImageIcon, story: Circle,
  post: FileText, citation: Quote, avant_apres: Images, temoignage: Sparkles,
};

function TileContent({ p, clientColor, Icon }) {
  // Vignette feed : visuel simple (preview WebP), 1re slide du carrousel (preview), couverture vidéo (preview)
  const sp = (p.slides_previews || [])[0];
  const raw = p.visual_preview_url || p.visual_url || (typeof sp === "string" ? sp : sp?.url) || (() => { const s = (p.slides || [])[0]; return typeof s === "string" ? s : s?.url; })() || "";
  const src = bustUrl(raw, p.updated_at);
  if (raw && isVideo(raw)) {
    const cover = bustUrl(p.cover_preview_url || p.cover_url, p.updated_at);
    if (cover) {
      return <img src={cover} alt={p.title} className="w-full h-full object-cover" loading="lazy" data-testid={`feed-cover-${p.id}`} />;
    }
    return <video src={src} muted playsInline preload="metadata" className="w-full h-full object-cover" data-testid={`feed-video-${p.id}`} />;
  }
  return src ? (
    <img src={src} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
  ) : (
    <div className="w-full h-full flex flex-col items-center justify-center p-4" style={{ background: clientColor }}>
      <Icon className="w-7 h-7 text-white/80 mb-2" />
      <span className="text-white/90 text-xs text-center leading-tight line-clamp-3 font-medium">{p.title}</span>
    </div>
  );
}

function DateBadge({ p }) {
  if (!p.date) return null;
  return (
    <span data-testid={`feed-date-${p.id}`}
      className="absolute bottom-2 left-2 z-10 text-[10px] font-medium bg-black/60 text-white rounded-full px-2 py-0.5 backdrop-blur-sm">
      {format(parseISO(p.date), "d MMM", { locale: fr })}
    </span>
  );
}

// Règle métier du feed : isFeedVisible (lib/constants) — jamais « idee », « a_creer »,
// « en_cours » ni les stories ; uniquement les visuels figés (à valider → publiée).
export default function FeedGrid({ pubs, clientColor = "#999", onPubClick, compact = false, showDate = false, variant = "grid" }) {
  const sorted = pubs.filter(isFeedVisible)
    .sort((a, b) => (b.date + (b.time || "")).localeCompare(a.date + (a.time || "")));
  if (sorted.length === 0) {
    return (
      <div className="text-center py-10 text-neutral-400 text-sm" data-testid="feed-empty">
        Aucune publication Instagram pour le moment.
      </div>
    );
  }

  if (variant === "column") {
    return (
      <div className="max-w-[440px] mx-auto space-y-6" data-testid="instagram-feed">
        {sorted.map((p) => {
          const Icon = FORMAT_ICONS[p.format] || ImageIcon;
          return (
            <article key={p.id} data-testid={`feed-cell-${p.id}`}
              className="bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-sm">
              <button onClick={() => onPubClick?.(p)}
                className="relative block w-full aspect-[3/4] overflow-hidden text-left group"
                style={{ background: p.visual_url ? "#111" : `${clientColor}22` }}
                title={`${p.title} — format 1080×1440`}>
                <TileContent p={p} clientColor={clientColor} Icon={Icon} />
                {(p.format === "carrousel" || p.format === "reel") && (
                  <div className="absolute top-2 right-2">
                    <Icon className="w-4 h-4 text-white drop-shadow" />
                  </div>
                )}
                <DateBadge p={p} />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </button>
              <div className="px-4 py-3">
                <p className="text-sm font-medium leading-snug">{p.title}</p>
                <p className="text-xs text-neutral-400 mt-1 capitalize">
                  {p.date ? format(parseISO(p.date), "d MMMM yyyy", { locale: fr }) : ""}
                  {p.time ? ` · ${p.time}` : ""} · {formatLabel(p.format)}
                </p>
              </div>
            </article>
          );
        })}
      </div>
    );
  }

  // Ordre façon feed Instagram : la publication la plus ancienne en bas à droite, puis les
  // suivantes de droite à gauche en remontant (la plus récente finit en haut à gauche).
  const sortedAsc = [...sorted].reverse();
  return (
    <div className="flex flex-row-reverse flex-wrap-reverse gap-1 rounded-xl overflow-hidden" data-testid="instagram-feed">
      {sortedAsc.map((p) => {
        const Icon = FORMAT_ICONS[p.format] || ImageIcon;
        return (
          <button
            key={p.id}
            data-testid={`feed-cell-${p.id}`}
            onClick={() => onPubClick?.(p)}
            className="feed-cell relative aspect-[3/4] overflow-hidden group text-left w-[calc(33.333%-2.67px)]"
            style={{ background: p.visual_url ? "#111" : `linear-gradient(135deg, ${clientColor}22, ${clientColor}55)` }}
            title={p.title}
          >
            <TileContent p={p} clientColor={clientColor} Icon={Icon} />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
              <div className="p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-[10px] font-medium leading-tight line-clamp-2">{p.title}</p>
                <p className="text-white/70 text-[9px] mt-0.5">{p.date} · {formatLabel(p.format)}</p>
              </div>
            </div>
            {(p.format === "carrousel" || p.format === "reel") && (
              <div className="absolute top-1.5 right-1.5">
                <Icon className="w-3.5 h-3.5 text-white drop-shadow" />
              </div>
            )}
            {showDate && <DateBadge p={p} />}
          </button>
        );
      })}
    </div>
  );
}
